import { useState } from 'react';
import { useGameStore } from '@hollowveil/shared';
import { adventures, type AdventureInfo } from '../../data/adventures';
import './TitleScreen.css';

type ScreenState = 'main' | 'adventure-select';

export function TitleScreen() {
  const { showTitleScreen, startNewGame, continueGame, hasSavedProgress, loadConfig } = useGameStore();
  const [screenState, setScreenState] = useState<ScreenState>('main');
  const [selectedAdventure, setSelectedAdventure] = useState<AdventureInfo | null>(null);

  if (!showTitleScreen) {
    return null;
  }

  const hasSave = hasSavedProgress();

  const handleNewGame = () => {
    setScreenState('adventure-select');
  };

  const handleSelectAdventure = (adventure: AdventureInfo) => {
    setSelectedAdventure(adventure);
  };

  const handleStartAdventure = () => {
    if (selectedAdventure) {
      // Charger la configuration de l'aventure sélectionnée
      loadConfig(selectedAdventure.config);
      startNewGame();
    }
  };

  const handleBack = () => {
    setScreenState('main');
    setSelectedAdventure(null);
  };

  // Écran de sélection d'aventure
  if (screenState === 'adventure-select') {
    return (
      <div className="title-screen">
        {/* Background decorations */}
        <div className="title-bg">
          <div className="floating-crystal crystal-1" />
          <div className="floating-crystal crystal-2" />
          <div className="floating-crystal crystal-3" />
          <div className="ghost-particle particle-1" />
          <div className="ghost-particle particle-2" />
          <div className="ghost-particle particle-3" />
        </div>

        {/* Adventure selection content */}
        <div className="title-content">
          <div className="title-logo">
            <h1 className="title-text" style={{ fontSize: '32px', letterSpacing: '4px' }}>
              CHOISIR UNE AVENTURE
            </h1>
          </div>

          {/* Adventure cards */}
          <div className="adventure-grid">
            {adventures.map((adventure) => (
              <button
                key={adventure.id}
                className={`adventure-card ${selectedAdventure?.id === adventure.id ? 'selected' : ''}`}
                onClick={() => handleSelectAdventure(adventure)}
              >
                <div className="adventure-icon">{adventure.icon}</div>
                <div className="adventure-info">
                  <h3 className="adventure-name">
                    {adventure.name}
                    {adventure.isNew && <span className="new-badge">NOUVEAU</span>}
                  </h3>
                  <p className="adventure-description">{adventure.description}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Action buttons */}
          <div className="title-menu">
            <button
              className="title-btn continue-btn"
              onClick={handleStartAdventure}
              disabled={!selectedAdventure}
              style={{ opacity: selectedAdventure ? 1 : 0.5 }}
            >
              <span className="btn-icon">&#x2728;</span>
              <span className="btn-text">Commencer</span>
            </button>

            <button className="title-btn new-game-btn" onClick={handleBack}>
              <span className="btn-icon">&#x2190;</span>
              <span className="btn-text">Retour</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Écran principal
  return (
    <div className="title-screen">
      {/* Background decorations */}
      <div className="title-bg">
        <div className="floating-crystal crystal-1" />
        <div className="floating-crystal crystal-2" />
        <div className="floating-crystal crystal-3" />
        <div className="ghost-particle particle-1" />
        <div className="ghost-particle particle-2" />
        <div className="ghost-particle particle-3" />
      </div>

      {/* Main content */}
      <div className="title-content">
        {/* Logo/Title */}
        <div className="title-logo">
          <div className="title-icon">
            <span className="icon-ghost">&#x1F47B;</span>
          </div>
          <h1 className="title-text">HOLLOWVEIL</h1>
          <p className="title-subtitle">Entre deux mondes</p>
        </div>

        {/* Menu buttons */}
        <div className="title-menu">
          {hasSave && (
            <button className="title-btn continue-btn" onClick={continueGame}>
              <span className="btn-icon">&#x25B6;</span>
              <span className="btn-text">Continuer</span>
            </button>
          )}

          <button className="title-btn new-game-btn" onClick={handleNewGame}>
            <span className="btn-icon">&#x2728;</span>
            <span className="btn-text">Nouvelle Partie</span>
          </button>
        </div>

        {/* Footer */}
        <div className="title-footer">
          <p>Utilise ZQSD ou touche pour te deplacer</p>
          <p>Appuie sur Espace ou Swipe pour changer de monde</p>
        </div>
      </div>
    </div>
  );
}
